import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  Chip,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  SelectChangeEvent,
  Tooltip,
  CircularProgress,
  Alert,
  Stack,
  InputAdornment
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ContentCopy as CopyIcon,
  Visibility as ViewIcon,
  MoreVert as MoreIcon,
  Search as SearchIcon,
  CheckCircle as ActiveIcon,
  Cancel as InactiveIcon,
  CloudUpload as UploadIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import formService from '../../services/formService';
import ImportFormDialog from '../../components/forms/ImportFormDialog';
import { formatDateTime } from '../../utils/dateUtils';

interface Form {
  id: number;
  name: string;
  description: string;
  version: number;
  active: boolean;
  is_template: boolean;
  total_categories: number;
  total_questions: number;
  total_sections: number;
  created_at: string;
  updated_at: string;
  creator_name?: string;
}

interface PaginationData {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

const FormList: React.FC = () => {
  const navigate = useNavigate();
  
  // Estados
  const [forms, setForms] = useState<Form[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  
  // Paginación
  const [pagination, setPagination] = useState<PaginationData>({
    page: 0,
    limit: 10,
    total: 0,
    totalPages: 0
  });
  
  // Filtros
  const [searchTerm, setSearchTerm] = useState('');
  const [filterActivo, setFilterActivo] = useState<string>('all');
  const [filterTemplate, setFilterTemplate] = useState<string>('all');
  
  // Menu de acciones
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedForm, setSelectedForm] = useState<Form | null>(null);
  
  // Diálogos
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [duplicateDialogOpen, setDuplicateDialogOpen] = useState(false);
  const [duplicateName, setDuplicateName] = useState('');
  const [importDialogOpen, setImportDialogOpen] = useState(false);

  // Cargar formularios
  const loadForms = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const params: any = {
        page: pagination.page + 1,
        limit: pagination.limit
      };
      
      if (searchTerm) params.search = searchTerm;
      if (filterActivo !== 'all') params.active = filterActivo === 'true';
      if (filterTemplate !== 'all') params.is_template = filterTemplate === 'true';
      
      const response = await formService.list(params);
      
      if (response.data) {
        setForms(response.data);
        setPagination(prev => ({
          ...prev,
          total: response.pagination.total,
          totalPages: response.pagination.totalPages
        }));
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Error al cargar formularios');
      console.error('Error loading forms:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadForms();
  }, [pagination.page, pagination.limit, searchTerm, filterActivo, filterTemplate]);

  // Handlers
  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
    setPagination(prev => ({ ...prev, page: 0 }));
  };

  const handleFilterActivoChange = (event: SelectChangeEvent) => {
    setFilterActivo(event.target.value);
    setPagination(prev => ({ ...prev, page: 0 }));
  };

  const handleFilterTemplateChange = (event: SelectChangeEvent) => {
    setFilterTemplate(event.target.value);
    setPagination(prev => ({ ...prev, page: 0 }));
  };

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPagination(prev => ({ ...prev, page: newPage }));
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setPagination(prev => ({
      ...prev,
      limit: parseInt(event.target.value, 10),
      page: 0
    }));
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, form: Form) => {
    setAnchorEl(event.currentTarget);
    setSelectedForm(form);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleCreate = () => {
    navigate('/forms/new');
  };

  const handleEdit = (form: Form) => {
    navigate(`/forms/${form.id}/edit`);
    handleMenuClose();
  };

  const handleView = (form: Form) => {
    navigate(`/forms/${form.id}/preview`);
    handleMenuClose();
  };

  const handleToggleStatus = async (form: Form) => {
    try {
      await formService.toggleStatus(form.id);
      setSuccessMessage(`Formulario ${form.active ? 'desactivado' : 'activado'} exitosamente`);
      loadForms();
      handleMenuClose();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Error al cambiar estado');
    }
  };

  const handleDuplicateOpen = (form: Form) => {
    setSelectedForm(form);
    setDuplicateName(`${form.name} (Copia)`);
    setDuplicateDialogOpen(true);
    handleMenuClose();
  };

  const handleDuplicateConfirm = async () => {
    if (!selectedForm) return;
    
    try {
      await formService.duplicate(selectedForm.id, {
        name: duplicateName
      });
      
      setSuccessMessage('Formulario duplicado exitosamente');
      loadForms();
      setDuplicateDialogOpen(false);
    } catch (err: any) {
      if (err.response?.status === 501) {
        setError('La funcionalidad de duplicación está en desarrollo. Se implementará en la próxima versión.');
        setDuplicateDialogOpen(false);
      } else {
        setError(err.response?.data?.error || 'Error al duplicar formulario');
      }
    }
  };

  const handleDeleteOpen = (form: Form) => {
    setSelectedForm(form);
    setDeleteDialogOpen(true);
    handleMenuClose();
  };

  const handleDeleteConfirm = async () => {
    if (!selectedForm) return;
    
    try {
      await formService.delete(selectedForm.id);
      setSuccessMessage('Formulario eliminado exitosamente');
      loadForms();
      setDeleteDialogOpen(false);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Error al eliminar formulario');
    }
  };

  const formatDate = (dateString: string) => {
    return formatDateTime(dateString);
  };

  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      {/* Header */}
      <Box sx={{ 
        mb: 3, 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        gap: { xs: 1, sm: 2 }
      }}>
        <Typography variant="h4" component="h1" sx={{ 
          fontSize: { xs: '1.25rem', sm: '2.125rem' },
          flexShrink: 1,
          minWidth: 0,
        }}>
          Formularios de Auditoría
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={<UploadIcon />}
            onClick={() => setImportDialogOpen(true)}
            sx={{
              fontSize: { xs: '0.75rem', sm: '0.875rem' },
              py: { xs: 0.75, sm: 1.5 },
              px: { xs: 1.5, sm: 3 },
              flexShrink: 0,
              whiteSpace: 'nowrap',
              borderColor: '#FFEB5D',
              color: '#000000',
              '&:hover': {
                borderColor: '#FFE135',
                backgroundColor: 'rgba(255, 235, 93, 0.1)',
              },
              '& .MuiButton-startIcon > svg': {
                color: '#FFEB5D',
              }
            }}
          >
            Importar
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }} />}
            onClick={handleCreate}
            sx={{
              fontSize: { xs: '0.75rem', sm: '0.875rem' },
              py: { xs: 0.75, sm: 1.5 },
              px: { xs: 1.5, sm: 3 },
              flexShrink: 0,
              whiteSpace: 'nowrap',
            }}
          >
            Crear Formulario
          </Button>
        </Box>
      </Box>

      {/* Alertas */}
      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {successMessage && (
        <Alert severity="success" onClose={() => setSuccessMessage(null)} sx={{ mb: 2 }}>
          {successMessage}
        </Alert>
      )}

      {/* Filtros */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center">
          <TextField
            placeholder="Buscar formularios..."
            value={searchTerm}
            onChange={handleSearchChange}
            size="small"
            fullWidth
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
          
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel id="estado-filter-label" htmlFor="estado-filter-input">Estado</InputLabel>
            <Select
              labelId="estado-filter-label"
              id="estado-filter"
              value={filterActivo}
              label="Estado"
              onChange={handleFilterActivoChange}
              inputProps={{
                name: 'estado',
                id: 'estado-filter-input',
              }}
            >
              <MenuItem value="all">Todos</MenuItem>
              <MenuItem value="true">Activos</MenuItem>
              <MenuItem value="false">Inactivos</MenuItem>
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel id="tipo-filter-label" htmlFor="tipo-filter-input">Tipo</InputLabel>
            <Select
              labelId="tipo-filter-label"
              id="tipo-filter"
              value={filterTemplate}
              label="Tipo"
              onChange={handleFilterTemplateChange}
              inputProps={{
                name: 'tipo',
                id: 'tipo-filter-input',
              }}
            >
              <MenuItem value="all">Todos</MenuItem>
              <MenuItem value="true">Plantillas</MenuItem>
              <MenuItem value="false">Formularios</MenuItem>
            </Select>
          </FormControl>
        </Stack>
      </Paper>

      {/* Tabla */}
      <TableContainer component={Paper}>
        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" p={4}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Nombre</TableCell>
                  <TableCell>Versión</TableCell>
                  <TableCell align="center">Secciones</TableCell>
                  <TableCell align="center">Categorías</TableCell>
                  <TableCell align="center">Preguntas</TableCell>
                  <TableCell>Estado</TableCell>
                  <TableCell>Tipo</TableCell>
                  <TableCell>Modificación</TableCell>
                  <TableCell align="right">Acciones</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {forms.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={9} align="center">
                      <Typography variant="body2" color="textSecondary" py={4}>
                        No se encontraron formularios
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  forms.map((form) => (
                    <TableRow key={form.id} hover>
                      <TableCell>
                        <Typography variant="body2" fontWeight={500}>
                          {form.name}
                        </Typography>

                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={`v${form.version}`} 
                          size="small" 
                          variant="outlined"
                          sx={{ borderColor: '#000000', color: '#000000' }}
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Chip 
                          label={form.total_sections || 0} 
                          size="small" 
                          variant="outlined"
                          sx={{ borderColor: '#000000', color: '#000000' }}
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Chip 
                          label={form.total_categories || 0} 
                          size="small" 
                          variant="outlined"
                          sx={{ borderColor: '#000000', color: '#000000' }}
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Chip 
                          label={form.total_questions || 0} 
                          size="small" 
                          variant="outlined"
                          sx={{ borderColor: '#000000', color: '#000000' }}
                        />
                      </TableCell>
                      <TableCell>
                        {form.active ? (
                          <Chip
                            icon={<ActiveIcon />}
                            label="Activo"
                            color="success"
                            size="small"
                          />
                        ) : (
                          <Chip
                            icon={<InactiveIcon />}
                            label="Inactivo"
                            color="default"
                            size="small"
                          />
                        )}
                      </TableCell>
                      <TableCell>
                        {form.is_template ? (
                          <Chip 
                            label="Plantilla" 
                            size="small" 
                            sx={{ 
                              backgroundColor: '#000000', 
                              color: '#ffffff',
                              fontWeight: 500
                            }} 
                          />
                        ) : (
                          <Chip 
                            label="Formulario" 
                            size="small" 
                            sx={{ 
                              backgroundColor: '#e0e0e0', 
                              color: '#000000',
                              fontWeight: 500
                            }}
                          />
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption">
                          {formatDate(form.updated_at)}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip title="Ver">
                          <IconButton
                            size="small"
                            onClick={() => handleView(form)}
                          >
                            <ViewIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Editar">
                          <IconButton
                            size="small"
                            onClick={() => handleEdit(form)}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <IconButton
                          size="small"
                          onClick={(e) => handleMenuOpen(e, form)}
                        >
                          <MoreIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
            
            <TablePagination
              component="div"
              count={pagination.total}
              page={pagination.page}
              onPageChange={handleChangePage}
              rowsPerPage={pagination.limit}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={[5, 10, 25, 50]}
              labelRowsPerPage="Filas por página:"
              labelDisplayedRows={({ from, to, count }) =>
                `${from}-${to} de ${count !== -1 ? count : `más de ${to}`}`
              }
              SelectProps={{
                inputProps: {
                  name: 'rows-per-page',
                  id: 'pagination-rows-per-page'
                }
              }}
            />
          </>
        )}
      </TableContainer>

      {/* Menu de acciones */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={() => selectedForm && handleView(selectedForm)}>
          <ViewIcon fontSize="small" sx={{ mr: 1 }} />
          Ver Detalles
        </MenuItem>
        <MenuItem onClick={() => selectedForm && handleEdit(selectedForm)}>
          <EditIcon fontSize="small" sx={{ mr: 1 }} />
          Editar
        </MenuItem>
        <MenuItem onClick={() => selectedForm && handleDuplicateOpen(selectedForm)}>
          <CopyIcon fontSize="small" sx={{ mr: 1 }} />
          Duplicar
        </MenuItem>
        <MenuItem onClick={() => selectedForm && handleToggleStatus(selectedForm)}>
          {selectedForm?.active ? (
            <>
              <InactiveIcon fontSize="small" sx={{ mr: 1 }} />
              Desactivar
            </>
          ) : (
            <>
              <ActiveIcon fontSize="small" sx={{ mr: 1 }} />
              Activar
            </>
          )}
        </MenuItem>
        <MenuItem 
          onClick={() => selectedForm && handleDeleteOpen(selectedForm)}
          sx={{ color: 'error.main' }}
        >
          <DeleteIcon fontSize="small" sx={{ mr: 1 }} />
          Eliminar
        </MenuItem>
      </Menu>

      {/* Diálogo de Duplicar */}
      <Dialog open={duplicateDialogOpen} onClose={() => setDuplicateDialogOpen(false)}>
        <DialogTitle>Duplicar Formulario</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Ingrese el nombre para el formulario duplicado:
          </DialogContentText>
          <TextField
            autoFocus
            fullWidth
            label="Nombre del formulario"
            value={duplicateName}
            onChange={(e) => setDuplicateName(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDuplicateDialogOpen(false)}>
            Cancelar
          </Button>
          <Button 
            onClick={handleDuplicateConfirm} 
            variant="contained"
            disabled={!duplicateName.trim()}
          >
            Duplicar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Diálogo de Eliminar */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Eliminar Formulario</DialogTitle>
        <DialogContent>
          <DialogContentText>
            ¿Está seguro que desea eliminar el formulario "{selectedForm?.name}"?
            Esta acción no se puede deshacer.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>
            Cancelar
          </Button>
          <Button 
            onClick={handleDeleteConfirm} 
            color="error"
            variant="contained"
          >
            Eliminar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Diálogo de Importar */}
      <ImportFormDialog
        open={importDialogOpen}
        onClose={() => setImportDialogOpen(false)}
        onSuccess={() => {
          setSuccessMessage('Formulario importado exitosamente');
          loadForms();
        }}
      />
    </Box>
  );
};

export default FormList;
